import { useEffect, useState } from "react";
import { useAuth } from "@/lib/AuthContext";
import {
  createAdminUser,
  deleteAdminUser,
  fetchAdminUsers,
  resetAdminPassword,
} from "@/lib/adminUsersApi";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import {
  Key,
  Trash2,
  Shield,
  UserPlus,
  Eye,
  EyeOff,
} from "lucide-react";

function fmt(d) {
  if (!d) return "—";
  return new Date(d).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function errStr(d) {
  if (!d) return "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(d);
}

export default function AdminUsers() {
  const { admin: currentAdmin } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [form, setForm] = useState({ email: "", password: "" });
  const [showPwd, setShowPwd] = useState(false);

  const load = () => {
    setLoading(true);
    fetchAdminUsers()
      .then(setList)
      .catch(() => setList([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const submitCreate = async (e) => {
    e.preventDefault();
    if (creating) return;
    if (form.password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    setCreating(true);
    try {
      await createAdminUser({ email: form.email.trim(), password: form.password });
      toast.success("Admin created.");
      setForm({ email: "", password: "" });
      setShowPwd(false);
      load();
    } catch (err) {
      toast.error(errStr(err?.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const confirmDelete = async (u) => {
    const ok = window.confirm(
      `Remove admin ${u.email}? They will lose access to /admin immediately.`
    );
    if (!ok) return;
    try {
      await deleteAdminUser(u.id);
      toast.success("Admin removed.");
      load();
    } catch (err) {
      toast.error(errStr(err?.response?.data?.detail));
    }
  };

  return (
    <div className="p-6 md:p-10" data-testid="admin-users-page">
      <header className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <div className="overline text-vermilion mb-3">Access</div>
          <h1 className="font-serif text-3xl md:text-4xl tracking-tight">
            Admins{" "}
            <span className="text-ink/40 font-mono text-sm align-middle">
              {loading ? "…" : `${list.length}`}
            </span>
          </h1>
          <p className="mt-3 text-sm text-ink/60 max-w-xl">
            Only admins listed here can sign in to <code className="font-mono text-ink">/admin</code>.
            Adding a new admin immediately grants them full dashboard access — including the ability
            to add more admins.
          </p>
        </div>
      </header>

      {/* Add form */}
      <section className="border border-border p-6 md:p-8 mb-10" data-testid="admin-users-create">
        <div className="flex items-center gap-2 overline text-vermilion mb-4">
          <UserPlus className="w-3.5 h-3.5" /> Add a new admin
        </div>
        <form onSubmit={submitCreate} className="grid grid-cols-1 md:grid-cols-12 gap-4">
          <div className="md:col-span-5 space-y-2">
            <Label htmlFor="new-email" className="overline text-ink/60">
              Email
            </Label>
            <Input
              id="new-email"
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="name@thinkright-tech.com"
              data-testid="new-admin-email"
              className="h-11 rounded-none border-ink/20"
            />
          </div>
          <div className="md:col-span-5 space-y-2">
            <Label htmlFor="new-password" className="overline text-ink/60">
              Password (min 8 chars)
            </Label>
            <div className="relative">
              <Input
                id="new-password"
                type={showPwd ? "text" : "password"}
                required
                minLength={8}
                value={form.password}
                onChange={(e) =>
                  setForm((f) => ({ ...f, password: e.target.value }))
                }
                placeholder="Choose a strong passphrase"
                data-testid="new-admin-password"
                className="h-11 rounded-none border-ink/20 pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPwd((v) => !v)}
                aria-label={showPwd ? "Hide password" : "Show password"}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-ink/40 hover:text-ink"
              >
                {showPwd ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
          <div className="md:col-span-2 flex items-end">
            <button
              type="submit"
              disabled={creating}
              data-testid="new-admin-submit"
              className="btn-primary w-full disabled:opacity-60"
            >
              {creating ? "Adding…" : "Add admin"}
            </button>
          </div>
        </form>
      </section>

      {/* List */}
      <div className="border-t border-border" data-testid="admin-users-list">
        <div className="grid grid-cols-12 gap-4 py-3 overline text-ink/40 border-b border-border px-2">
          <div className="col-span-5">Email</div>
          <div className="col-span-3">Created</div>
          <div className="col-span-2">Added by</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>

        {loading && <div className="py-16 text-center text-ink/50">Loading…</div>}
        {!loading && list.length === 0 && (
          <div className="py-16 text-center text-ink/50">No admins yet.</div>
        )}

        <ul>
          {list.map((u) => {
            const isMe = u.id === currentAdmin?.id;
            return (
              <li
                key={u.id}
                data-testid={`admin-row-${u.id}`}
                className="grid grid-cols-12 gap-4 py-5 px-2 border-b border-border items-center"
              >
                <div className="col-span-5 min-w-0 flex items-center gap-3">
                  <div className="w-9 h-9 bg-ink text-white flex items-center justify-center">
                    <Shield className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-serif text-lg truncate">
                      {u.email}{" "}
                      {isMe && (
                        <span className="ml-2 inline-block text-[10px] uppercase tracking-[0.18em] text-vermilion align-middle">
                          You
                        </span>
                      )}
                      {u.is_seed && (
                        <span className="ml-2 inline-block text-[10px] uppercase tracking-[0.18em] text-ink/50 border border-ink/20 px-1.5 py-0.5 align-middle">
                          Seed
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-ink/50">{u.role}</div>
                  </div>
                </div>
                <div className="col-span-3 text-xs text-ink/60">
                  {fmt(u.created_at)}
                </div>
                <div className="col-span-2 text-xs text-ink/60 truncate">
                  {u.created_by || "—"}
                </div>
                <div className="col-span-2 flex justify-end gap-2">
                  <button
                    onClick={() => setResetTarget(u)}
                    data-testid={`reset-pwd-${u.id}`}
                    title="Reset password"
                    className="inline-flex items-center gap-1.5 px-3 py-2 border border-ink/20 text-[10px] uppercase tracking-[0.18em] hover:bg-muted"
                  >
                    <Key className="w-3.5 h-3.5" /> Password
                  </button>
                  {!isMe && !u.is_seed && (
                    <button
                      onClick={() => confirmDelete(u)}
                      data-testid={`delete-admin-${u.id}`}
                      title="Remove admin"
                      className="inline-flex items-center gap-1.5 px-3 py-2 border border-vermilion/40 text-vermilion text-[10px] uppercase tracking-[0.18em] hover:bg-vermilion hover:text-white transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Remove
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Reset password drawer */}
      <ResetPasswordDrawer
        target={resetTarget}
        onClose={(didChange) => {
          setResetTarget(null);
          if (didChange) load();
        }}
      />
    </div>
  );
}

function ResetPasswordDrawer({ target, onClose }) {
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!target) {
      setPwd("");
      setShow(false);
    }
  }, [target]);

  const submit = async (e) => {
    e.preventDefault();
    if (!target) return;
    if (pwd.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    setBusy(true);
    try {
      await resetAdminPassword(target.id, pwd);
      toast.success(`Password reset for ${target.email}.`);
      onClose(true);
    } catch (err) {
      toast.error(errStr(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={!!target} onOpenChange={(v) => !v && onClose(false)}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md rounded-none border-l border-border p-0"
        data-testid="reset-password-drawer"
      >
        {target && (
          <form onSubmit={submit} className="flex flex-col h-full">
            <SheetHeader className="p-8 border-b border-border">
              <div className="overline text-vermilion mb-2">Reset password</div>
              <SheetTitle className="font-serif text-3xl leading-tight tracking-tight">
                {target.email}
              </SheetTitle>
              <SheetDescription className="text-sm text-ink/60 mt-1">
                This takes effect immediately. The admin will need to sign in
                again with the new password.
              </SheetDescription>
            </SheetHeader>

            <div className="flex-1 p-8 space-y-6">
              <div className="space-y-2">
                <Label htmlFor="reset-pwd" className="overline text-ink/60">
                  New password (min 8 chars)
                </Label>
                <div className="relative">
                  <Input
                    id="reset-pwd"
                    type={show ? "text" : "password"}
                    required
                    minLength={8}
                    autoFocus
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    data-testid="reset-password-input"
                    className="h-11 rounded-none border-ink/20 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShow((v) => !v)}
                    aria-label={show ? "Hide password" : "Show password"}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-ink/40 hover:text-ink"
                  >
                    {show ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
                <p className="text-xs text-ink/50">
                  Share this password with the admin over a secure channel. We
                  do not email it for you.
                </p>
              </div>
            </div>

            <div className="p-8 border-t border-border flex gap-3">
              <button
                type="button"
                onClick={() => onClose(false)}
                className="btn-outline flex-1"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={busy}
                data-testid="reset-password-submit"
                className="btn-primary flex-1 disabled:opacity-60"
              >
                {busy ? "Saving…" : "Save password"}
              </button>
            </div>
          </form>
        )}
      </SheetContent>
    </Sheet>
  );
}
